import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "../store";
import { Cemetery } from "../types/cemetery";
import { cemeterySearchApi } from "./cemeterySearchSlice";

export interface CemeteryInput {
  name: string;
  state: string;
  city?: string;
  lat?: string;
  lng?: string;
}

interface ManagedCemeteryResponse {
  cemeteries: Cemetery[];
  states: string[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export const cemeterySlice = createApi({
  reducerPath: "cemeterySlice",
  baseQuery: fetchBaseQuery({
    baseUrl: import.meta.env.VITE_API_BASE_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) headers.set("Authorization", `Bearer ${token}`);
      return headers;
    },
  }),
  // cemetery data rarely changes, keep cached for 30 minutes
  keepUnusedDataFor: 1800,
  refetchOnMountOrArgChange: 300, // only refetch if older than 5 minutes
  refetchOnFocus: false, // don't refetch just because window gets focus
  refetchOnReconnect: false, // cemeteries don't change often, no need to refetch
  tagTypes: ["Cemetery"],
  endpoints: (builder) => ({
    getCemeteries: builder.query<Cemetery[], void>({
      query: () => "/cemeteries",
      providesTags: ["Cemetery"],
    }),
    getManagedCemeteries: builder.query<
      ManagedCemeteryResponse,
      { state?: string; query?: string }
    >({
      query: ({ state, query }) => ({
        url: "/cemeteries/manage",
        params: {
          ...(state && { state }),
          ...(query && { q: query }),
        },
      }),
      providesTags: ["Cemetery"],
    }),
    createCemetery: builder.mutation<Cemetery, CemeteryInput>({
      query: (cemetery) => ({
        url: "/cemeteries",
        method: "POST",
        body: cemetery,
      }),
      invalidatesTags: ["Cemetery"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cemeterySearchApi.util.invalidateTags(["Cemetery"]));
        } catch {
          // The caller presents the error to the user.
        }
      },
    }),
    updateCemetery: builder.mutation<Cemetery, CemeteryInput & { id: string }>({
      query: ({ id, ...cemetery }) => ({
        url: `/cemeteries/${id}`,
        method: "PUT",
        body: cemetery,
      }),
      invalidatesTags: ["Cemetery"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cemeterySearchApi.util.invalidateTags(["Cemetery"]));
        } catch {
          // The caller presents the error to the user.
        }
      },
    }),
    deleteCemetery: builder.mutation<void, string>({
      query: (id) => ({
        url: `/cemeteries/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["Cemetery"],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(cemeterySearchApi.util.invalidateTags(["Cemetery"]));
        } catch {
          // The caller presents the error to the user.
        }
      },
    }),
  }),
});

export const {
  useGetCemeteriesQuery,
  useGetManagedCemeteriesQuery,
  useCreateCemeteryMutation,
  useUpdateCemeteryMutation,
  useDeleteCemeteryMutation,
  // export prefetch methods for manual prefetching
  usePrefetch,
} = cemeterySlice;
